import cn from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { OfferingSwitchConfirmation } from 'legacy/lut/components/widgets/public/partner360/programTypeButtons/components/programTypeButtonsControl/components/offeringSwitchConfirmation/OfferingSwitchConfirmation'
import { IProgramType, selectProgramTypes } from 'legacy/lut/features/partner360/configuration'
import { useId, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Nullable } from 'clickNgo/models/utils'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Switch } from 'core-components/library/checkbox'
import { OfferingIcon } from 'partner360/components/offeringWithIcon'

import { PROGRAM_TYPE_TAB_ICON_SIZES } from '../../../../configs/programTypeTabIconSizes'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getOfferingTitleKey } from '../../../../lib/getOfferingTitleKey'
import { getSwitchableCockpitOfferings } from '../../../../lib/getSwitchableCockpitOfferings'
import { useCanSwitchCockpitOffering, useCockpitOfferingSwitch } from '../../../../model/hooks'
import {
  selectCockpitFirstPartnerOfferingsCatalog,
  selectCockpitOffering,
} from '../../../../model/selectors'
import { ChevronIcon, GridIcon } from '../../lib/icons'

import styles from './HelpPanelOfferings.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.helpPanel

interface IHelpPanelOfferingsProps {
  offerings: IProgramType[]
  selectedKey?: ProgramTypes
  disabled?: boolean
  loadingKey?: Nullable<ProgramTypes>
  /** Whether the user may switch this offering; one they may not keeps its switch, greyed. */
  canSwitch?: (offering: IProgramType) => boolean
  onToggle?: (offering: IProgramType, flag: boolean) => void
}

/** Which offerings the partner plans, folded to one row: how many are on and their icons, the
 *  ones switched off greyed. Unfolded, every offering has its own switch. */
const HelpPanelOfferings = ({
  offerings,
  selectedKey,
  disabled = false,
  loadingKey,
  canSwitch,
  onToggle,
}: IHelpPanelOfferingsProps) => {
  const t = useSafetyTranslation()
  const bodyId = useId()
  const [isOpen, setIsOpen] = useState(false)
  const enabledCount = offerings.filter(({ enabled }) => enabled).length

  if (offerings.length === 0) {
    return null
  }

  const icon = (programType: ProgramTypes) => (
    <OfferingIcon offering={programType} sizes={PROGRAM_TYPE_TAB_ICON_SIZES} />
  )

  return (
    <div className={cn(styles.root, { [styles.open]: isOpen, [styles.disabled]: disabled })}>
      <button
        type="button"
        className={styles.header}
        aria-expanded={isOpen}
        aria-controls={bodyId}
        disabled={disabled}
        onClick={() => setIsOpen((value) => !value)}
      >
        <GridIcon className={styles.headerIcon} />
        <span>
          <span className={styles.title}>
            <Translation text={KEYS.offerings} span />
          </span>
          <small className={styles.subtitle}>
            <Translation
              text={KEYS.offeringsOn}
              replaceParams={[
                { key: '{count}', value: String(enabledCount) },
                { key: '{total}', value: String(offerings.length) },
              ]}
              span
            />
          </small>
        </span>
        <span className={styles.icons} aria-hidden="true">
          {offerings.map(({ programType, enabled }) => (
            <span key={programType} className={cn({ [styles.iconOff]: !enabled })}>
              {icon(programType)}
            </span>
          ))}
        </span>
        <ChevronIcon className={styles.chevron} />
      </button>

      <div id={bodyId} className={styles.body} inert={!isOpen}>
        <div>
          <p className={styles.hint}>
            <Translation text={KEYS.offeringsHint} span />
          </p>
          {offerings.map((offering) => (
            <div
              key={offering.programType}
              className={cn(styles.row, {
                [styles.rowOff]: !offering.enabled,
                [styles.rowCurrent]: offering.programType === selectedKey,
              })}
            >
              <span className={styles.rowIcon}>{icon(offering.programType)}</span>
              <span className={styles.rowTitle}>
                <Translation text={getOfferingTitleKey(offering.programType)} span />
              </span>
              <span aria-label={t(getOfferingTitleKey(offering.programType))}>
                <Switch
                  checked={offering.enabled}
                  disabled={
                    disabled ||
                    loadingKey === offering.programType ||
                    (canSwitch ? !canSwitch(offering) : false)
                  }
                  onChange={(flag) => onToggle?.(offering, flag)}
                  size="sm"
                />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** The cockpit's offerings: switching one asks for confirmation, then reloads its plan. */
export const CockpitHelpOfferings = ({ partnerId }: { partnerId: string }) => {
  const offeringsCatalog = useSelector(selectCockpitFirstPartnerOfferingsCatalog)
  const selectedOffering = useSelector(selectCockpitOffering)
  const offerings = useMemo(
    () => getSwitchableCockpitOfferings(offeringsCatalog),
    [offeringsCatalog]
  )
  const canSwitch = useCanSwitchCockpitOffering(offerings)
  const {
    isDistributor,
    loadingOffering,
    pendingChange,
    requestChange,
    confirmChange,
    cancelChange,
  } = useCockpitOfferingSwitch(partnerId)

  return (
    <>
      <HelpPanelOfferings
        offerings={offerings}
        selectedKey={selectedOffering}
        loadingKey={loadingOffering}
        canSwitch={canSwitch}
        onToggle={(offering, flag) => requestChange({ offering, flag })}
      />
      <OfferingSwitchConfirmation
        isShow={pendingChange !== null}
        isDistributor={isDistributor}
        state={pendingChange}
        onConfirm={confirmChange}
        onCancel={cancelChange}
      />
    </>
  )
}

/** The classic page's offerings, shown as they are and greyed: they are switched in the cockpit. */
export const ClassicHelpOfferings = () => {
  const programTypes = useSelector(selectProgramTypes)
  const offerings = useMemo(() => getSwitchableCockpitOfferings(programTypes), [programTypes])

  return <HelpPanelOfferings offerings={offerings} disabled />
}
